import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { onForegroundMessage, refreshPushTokenIfGranted } from "./lib/push";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Deals from "./pages/Deals";
import Checklist from "./pages/Checklist";
import TemplateEditor from "./pages/TemplateEditor";
import Settings from "./pages/Settings";

interface Toast {
  id: number;
  title: string;
  body: string;
  url: string;
}

function Shell() {
  const { user, loading, domainOk } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Re-save the FCM token on login when permission is already granted, and
  // route notification clicks (posted by the SW) to their target URL.
  useEffect(() => {
    if (!user || !domainOk) return;
    // Register the SW unconditionally (not just when push is enabled) so the
    // app is PWA-installable — a prerequisite for iOS web push.
    navigator.serviceWorker
      ?.register("/firebase-messaging-sw.js")
      .catch((err) => console.warn("[sw] registration failed:", err));
    void refreshPushTokenIfGranted();
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "NOTIFICATION_CLICK" && typeof event.data.url === "string") {
        const url = new URL(event.data.url, window.location.origin);
        navigate(url.pathname + url.search);
      }
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, [user, domainOk, navigate]);

  // Foreground FCM messages reach the page, not the SW — render them as
  // in-app toasts so focused-tab notifications aren't silently dropped.
  useEffect(() => {
    if (!user || !domainOk) return;
    let unsub: (() => void) | undefined;
    let cancelled = false;
    void onForegroundMessage((data) => {
      const toast: Toast = { id: Date.now() + Math.random(), ...data };
      setToasts((t) => [...t, toast]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== toast.id)), 8000);
    }).then((u) => {
      if (cancelled) u();
      else unsub = u;
    });
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [user, domainOk]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <span className="eyebrow">Loading…</span>
      </div>
    );
  }
  if (!user || !domainOk) return <Login />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Deals />} />
        <Route path="/deals/:dealId" element={<Checklist />} />
        <Route path="/template" element={<TemplateEditor />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Foreground notification toasts */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2">
          {toasts.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setToasts((all) => all.filter((x) => x.id !== t.id));
                const url = new URL(t.url, window.location.origin);
                navigate(url.pathname + url.search);
              }}
              className="rounded-[10px] border border-line bg-white p-3 text-left shadow-xl transition-transform hover:-translate-y-0.5"
            >
              <div className="text-[12px] font-semibold text-ink">{t.title}</div>
              {t.body && <div className="mt-0.5 text-[12px] text-ink-soft">{t.body}</div>}
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
}

export default function App() {
  return <Shell />;
}
